import dayjs from "dayjs";
import localeData from "dayjs/plugin/localeData";
import localizedFormat from "dayjs/plugin/localizedFormat";
import { ReactNode, useEffect, useState } from "react";

import { useTranslation } from "@/core/i18n";
import { TextField } from "atoms/text-field/TextField";
import { formatToLongDate } from "utils/dayjsUtils";
import { isValidDateFormat } from "utils/validators";

// Initialize dayjs plugins
dayjs.extend(localeData);
dayjs.extend(localizedFormat);

// Ensure dayjs has a default locale set for formatToLongDate to work
if (!dayjs.locale() || dayjs.locale() === "en") {
  dayjs.locale("en");
}

export interface DateOfBirthFieldProps {
  /** The current value as ISO date string */
  value?: string;
  /** Callback when date changes - receives ISO string or null */
  onChange?: (value: string | null) => void;
  /** Callback when validation state changes */
  onValidationChange?: (isValid: boolean) => void;
  /** Callback when field loses focus */
  onBlur?: () => void;
  /** Error message to display */
  error?: string;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Optional text or content to show below the field */
  suffixText?: ReactNode;
  /** Custom label for the field */
  label?: string;
  /** Custom className for the input field */
  className?: string;
  /** Custom className for the label */
  labelClassName?: string;
  /**
   * Defaults true for the dedicated date-of-birth step, where this is the only field on screen.
   * Pass false anywhere it sits among other inputs: focusing on mount pulls the caret out of
   * whatever the patient is already typing, and the rest of their keystrokes land here instead.
   */
  autoFocus?: boolean;
  optional?: boolean;
}

export const DateOfBirthField = ({
  value,
  onChange,
  onValidationChange,
  onBlur,
  error,
  disabled = false,
  suffixText,
  label,
  className,
  labelClassName,
  autoFocus = true,
  optional = false,
}: DateOfBirthFieldProps) => {
  const { t } = useTranslation();
  const displayLabel = label ?? t("dateOfBirthField.label");
  // Track user input while typing (undefined means use computed display value)
  const [inputValue, setInputValue] = useState<string | undefined>();

  // Computed display value from prop
  const displayValue =
    inputValue ?? (value ? dayjs(value.slice(0, 10), "YYYY-MM-DD", true).format("L") : "");

  // Reset input value when parent value changes
  useEffect(() => {
    setInputValue(undefined);
  }, [value]);

  // Validate initial value
  useEffect(() => {
    if (value) {
      onValidationChange?.(isValidDateFormat(value.slice(0, 10), "YYYY-MM-DD"));
    } else {
      onValidationChange?.(optional);
    }
  }, [value, onValidationChange, optional]);

  const handleChange = (inputValue: string) => {
    // Format the input as user types
    const formatted = isValidDateFormat(inputValue, "YYYY-MM-DD")
      ? dayjs(inputValue, "YYYY-MM-DD", true).format("L")
      : formatToLongDate(inputValue);
    setInputValue(formatted);

    // Validate and call onChange if valid
    if (formatted.length >= 10 && isValidDateFormat(formatted)) {
      const parsed = dayjs(formatted, dayjs.localeData().longDateFormat("L"), true);
      const isoDate = new Date(
        Date.UTC(parsed.year(), parsed.month(), parsed.date()),
      ).toISOString();
      onChange?.(isoDate);
      onValidationChange?.(true);
    } else if (formatted === "") {
      // Handle empty input
      onChange?.(null);
      onValidationChange?.(optional);
    } else {
      // For invalid/incomplete dates, mark as invalid
      onValidationChange?.(false);
    }
  };

  const handleBlur = () => {
    onBlur?.();
  };

  return (
    <div className="w-full">
      <TextField
        type="text"
        label={displayLabel}
        name="dateOfBirth"
        autoComplete="bday"
        inputMode="numeric"
        value={displayValue}
        maxLength={10}
        autoFocus={autoFocus}
        placeholder={dayjs.localeData().longDateFormat("L")}
        invalid={!!error}
        supportingText={error}
        disabled={disabled}
        onChange={handleChange}
        onBlur={handleBlur}
        className={className}
        labelClassName={labelClassName}
      />
      {suffixText && (
        <div className="mb-1 mt-4 text-center text-body2 text-secondary">{suffixText}</div>
      )}
    </div>
  );
};
